import { HTTPStatusCode } from './statuses';

/**
 * An error that can be exposed to the user
 */
export abstract class ExposableError extends Error {
    /**
     * Expose is set to false to prevent these errors being shown to
     * the client.
     */
    public expose = false;

    constructor(error: string | Error, expose?: boolean, options?: ErrorOptions) {
        super(error instanceof Error ? error.message : error, options);
        this.expose = expose === undefined ? false : expose;
    }
}

/** Class for all HTTP errors. */
export class HTTPError extends ExposableError {
    public name: string;

    /** the status code of the error, defaulting to 500 */
    public readonly statusCode: HTTPStatusCode;

    /** @deprecated  the status code of the error, mirroring statusCode for general compatibility */
    public readonly status: HTTPStatusCode;
    /** @deprecated  the status code of the error, mirroring statusCode for general compatibility */
    public readonly code: HTTPStatusCode;

    /** Headers to attach to the response */
    public headers: Record<string, string> = {};

    constructor(
        error: string | Error,
        statusCode: HTTPStatusCode,
        expose?: boolean,
        options?: ErrorOptions
    ) {
        super(
            error,
            expose === undefined ? statusCode < HTTPStatusCode.InternalServerError : expose,
            options
        );
        this.name = HTTPStatusCode[statusCode];
        this.statusCode = this.status = this.code = statusCode;
    }
}

/** Type when the error could be an HTTPError but also another JS error */
export type MaybeHTTPError = HTTPError | (Error & { statusCode?: undefined; expose?: undefined });

/** Create an HTTPError */
function fromStatusCode(status: HTTPStatusCode, error: string | Error): HTTPError {
    return new HTTPError(error, status);
}

function createHTTPErrorClass(statusCode: HTTPStatusCode) {
    return class extends HTTPError {
        constructor(error: string | Error, expose?: boolean, options?: ErrorOptions) {
            super(error, statusCode, expose, options);
        }
    };
}

export class BadRequest extends createHTTPErrorClass(HTTPStatusCode.BadRequest) {}
export class Unauthorized extends createHTTPErrorClass(HTTPStatusCode.Unauthorized) {}
export class PaymentRequired extends createHTTPErrorClass(HTTPStatusCode.PaymentRequired) {}
export class Forbidden extends createHTTPErrorClass(HTTPStatusCode.Forbidden) {}
export class NotFound extends createHTTPErrorClass(HTTPStatusCode.NotFound) {}
export class MethodNotAllowed extends createHTTPErrorClass(HTTPStatusCode.MethodNotAllowed) {}
export class NotAcceptable extends createHTTPErrorClass(HTTPStatusCode.NotAcceptable) {}
export class ProxyAuthenticationRequired extends createHTTPErrorClass(
    HTTPStatusCode.ProxyAuthenticationRequired
) {}
export class RequestTimeout extends createHTTPErrorClass(HTTPStatusCode.RequestTimeout) {}
export class Conflict extends createHTTPErrorClass(HTTPStatusCode.Conflict) {}
export class Gone extends createHTTPErrorClass(HTTPStatusCode.Gone) {}
export class LengthRequired extends createHTTPErrorClass(HTTPStatusCode.LengthRequired) {}
export class PreconditionFailed extends createHTTPErrorClass(HTTPStatusCode.PreconditionFailed) {}
export class PayloadTooLarge extends createHTTPErrorClass(HTTPStatusCode.PayloadTooLarge) {}
export class URITooLong extends createHTTPErrorClass(HTTPStatusCode.URITooLong) {}
export class UnsupportedMediaType extends createHTTPErrorClass(
    HTTPStatusCode.UnsupportedMediaType
) {}
export class RangeNotSatisfiable extends createHTTPErrorClass(HTTPStatusCode.RangeNotSatisfiable) {}
export class ExpectationFailed extends createHTTPErrorClass(HTTPStatusCode.ExpectationFailed) {}
export class ImATeapot extends createHTTPErrorClass(HTTPStatusCode.ImATeapot) {}
export class MisdirectedRequest extends createHTTPErrorClass(HTTPStatusCode.MisdirectedRequest) {}
export class UnprocessableEntity extends createHTTPErrorClass(HTTPStatusCode.UnprocessableEntity) {}
export class Locked extends createHTTPErrorClass(HTTPStatusCode.Locked) {}
export class FailedDependency extends createHTTPErrorClass(HTTPStatusCode.FailedDependency) {}
export class UnorderedCollection extends createHTTPErrorClass(HTTPStatusCode.UnorderedCollection) {}
export class UpgradeRequired extends createHTTPErrorClass(HTTPStatusCode.UpgradeRequired) {}
export class PreconditionRequired extends createHTTPErrorClass(HTTPStatusCode.PreconditionFailed) {}
export class TooManyRequests extends createHTTPErrorClass(HTTPStatusCode.TooManyRequests) {}
export class RequestHeaderFieldsTooLarge extends createHTTPErrorClass(
    HTTPStatusCode.RequestHeaderFieldsTooLarge
) {}
export class UnavailableForLegalReasons extends createHTTPErrorClass(
    HTTPStatusCode.UnavailableForLegalReasons
) {}
export class InternalServerError extends createHTTPErrorClass(HTTPStatusCode.InternalServerError) {}
export class NotImplemented extends createHTTPErrorClass(HTTPStatusCode.NotImplemented) {}
export class BadGateway extends createHTTPErrorClass(HTTPStatusCode.BadGateway) {}
export class ServiceUnavailable extends createHTTPErrorClass(HTTPStatusCode.ServiceUnavailable) {}
export class GatewayTimeout extends createHTTPErrorClass(HTTPStatusCode.GatewayTimeout) {}
export class HTTPVersionNotSupported extends createHTTPErrorClass(
    HTTPStatusCode.HTTPVersionNotSupported
) {}
export class VariantAlsoNegotiates extends createHTTPErrorClass(
    HTTPStatusCode.VariantAlsoNegotiates
) {}
export class InsufficientStorage extends createHTTPErrorClass(HTTPStatusCode.InsufficientStorage) {}
export class LoopDetected extends createHTTPErrorClass(HTTPStatusCode.LoopDetected) {}
export class BandwidthLimitExceeded extends createHTTPErrorClass(
    HTTPStatusCode.BandwidthLimitExceeded
) {}
export class NotExtended extends createHTTPErrorClass(HTTPStatusCode.NotExtended) {}
export class NetworkAuthenticationRequired extends createHTTPErrorClass(
    HTTPStatusCode.NetworkAuthenticationRequired
) {}

export const createHTTPError = {
    fromStatusCode,
    BadRequest,
    Unauthorized,
    PaymentRequired,
    Forbidden,
    NotFound,
    MethodNotAllowed,
    NotAcceptable,
    ProxyAuthenticationRequired,
    RequestTimeout,
    Conflict,
    Gone,
    LengthRequired,
    PreconditionFailed,
    PayloadTooLarge,
    URITooLong,
    UnsupportedMediaType,
    RangeNotSatisfiable,
    ExpectationFailed,
    ImATeapot,
    MisdirectedRequest,
    UnprocessableEntity,
    Locked,
    FailedDependency,
    UnorderedCollection,
    UpgradeRequired,
    PreconditionRequired,
    TooManyRequests,
    RequestHeaderFieldsTooLarge,
    UnavailableForLegalReasons,
    InternalServerError,
    NotImplemented,
    BadGateway,
    ServiceUnavailable,
    GatewayTimeout,
    HTTPVersionNotSupported,
    VariantAlsoNegotiates,
    InsufficientStorage,
    LoopDetected,
    BandwidthLimitExceeded,
    NotExtended,
    NetworkAuthenticationRequired,
};
